import React, { useEffect, useState, useContext, useCallback } from "react";

import Input from "shared/components/FormElements/Input";
import { useTranslation } from "react-i18next";
import CustomSelect from "shared/components/FormElements/Select";
import NotFound from "shared/components/UIElements/NotFound";
import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";
import FormFooter from "shared/components/FormElements/FormFooter";

import { VALIDATOR_REQUIRE } from "shared/util/validators";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";

import { discountTypes } from "shared/util/types";

const DiscountItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  const { t } = useTranslation();
  //standard code
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const itemType = "discount";
  const endPointUpdate = "discounts";
  const endPointDelete = "discounts";
  const endPointCreate = "discounts";

  const { discounts } = useContext(StoreContext);

  const [formState, inputHandler] = useForm(
    {
      name: { value: null, isValid: true },
      type: { value: null, isValid: true },
      amount: { value: null, isValid: true },
    },
    true
  );

  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const retrievedItem = discounts.find((item) => item.id === itemId);
      setLoadedItem(retrievedItem);
    } catch (err) {}
    setIsItemLoaded(true);
  }, [discounts, itemId]);

  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    const endpoint = isNewItem
      ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
      : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

    const postType = isNewItem ? "POST" : "PATCH";

    const infoToBeSent = {
      name: formState.inputs.name.value,
      type: formState.inputs.type.value,
      amount: formState.inputs.amount.value,
    };

    try {
      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
        } else {
          UpdateItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      if (err?.message) {
        window.toast.error(err.message);
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  };

  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item={itemType}></NotFound>;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form className="form-container" onSubmit={itemUpdateSubmitHandler}>
          <Input
            id="name"
            element="input"
            type="text"
            label="İndirim Adı"
            validators={[VALIDATOR_REQUIRE()]}
            errorText="İndirim adı giriniz"
            initialValue={loadedItem?.name || ""}
            initialValid={isNewItem ? false : true}
            onInput={inputHandler}
          />

          <CustomSelect
            options={discountTypes}
            id="type"
            onInput={inputHandler}
            label="İndirim Türü"
            initialValid={true}
            validators={[VALIDATOR_REQUIRE()]}
            initialValue={
              discountTypes?.find((item) => item?.value?.toString() === loadedItem?.type?.toString()) || null
            }
          ></CustomSelect>

          <Input
            id="amount"
            element="input"
            type="text"
            label="İndirim Miktarı"
            validators={[VALIDATOR_REQUIRE()]}
            errorText="İndirim miktarı giriniz"
            initialValue={loadedItem?.amount || ""}
            initialValid={isNewItem ? false : true}
            onInput={inputHandler}
          />

          <FormFooter
            isNewItem={isNewItem}
            onUpdate={itemUpdateSubmitHandler}
            onDelete={() => setShowConfirmModal(true)}
            isUpdating={isLoading}
            disabled={!formState.isValid || isLoading || !formState.inputs.type.value}
          />
        </form>
      )}
    </React.Fragment>
  );
};

export default DiscountItem;
